import { Graphics } from 'pixi.js';
import type { Application } from 'pixi.js';
import type { World, ComponentStore } from '@ecs/world';
import { DEFAULT_TUNING, type Tuning } from '@game/tuning';
import type {
	AmmoComponent,
	CameraResource,
	EnemyComponent,
	PlatformComponent,
	PlatformResource,
	StageBounds,
	TransformComponent
} from '@game/scenes/characterPrototype/types';
import { randomInRange } from '@game/scenes/characterPrototype/math';

export type SpawnPlatformFn = (
	world: World,
	platforms: ComponentStore<PlatformComponent>,
	graphics: ComponentStore<Graphics>,
	stage: Application['stage'],
	x: number,
	y: number,
	width: number,
	height: number
) => number;

export type SpawnEnemyFn = (
	world: World,
	enemies: ComponentStore<EnemyComponent>,
	transforms: ComponentStore<TransformComponent>,
	graphics: ComponentStore<Graphics>,
	ammoGraphics: ComponentStore<Graphics>,
	ammos: ComponentStore<AmmoComponent>,
	stage: Application['stage'],
	x: number,
	y: number
) => void;

export function spawnInitialPlatforms(
	world: World,
	platforms: ComponentStore<PlatformComponent>,
	graphics: ComponentStore<Graphics>,
	stage: Application['stage'],
	bounds: StageBounds,
	basePlatform: PlatformResource,
	tuning: Tuning,
	spawnPlatform: SpawnPlatformFn
) {
	let nextY = world.getResource<number>('platform-next-y') ?? basePlatform.y - tuning.platformGapMin;
	const camera = world.getResource<CameraResource>('camera') ?? { y: 0, followThreshold: tuning.cameraFollowThreshold, peakY: 0 };
	while (nextY > camera.y - tuning.cameraSpawnAhead) {
		const width = randomInRange(tuning.platformMinWidth, tuning.platformMaxWidth);
		const maxX = Math.max(tuning.platformMinSideOffset, bounds.width - tuning.platformMinSideOffset - width);
		const x = randomInRange(tuning.platformMinSideOffset, maxX);
		spawnPlatform(world, platforms, graphics, stage, x, nextY, width, tuning.platformHeight);
		nextY -= randomInRange(tuning.platformGapMin, tuning.platformGapMax);
	}
	world.setResource<number>('platform-next-y', nextY);
}

export function spawnInitialEnemies(
	world: World,
	enemies: ComponentStore<EnemyComponent>,
	transforms: ComponentStore<TransformComponent>,
	graphics: ComponentStore<Graphics>,
	ammoGraphics: ComponentStore<Graphics>,
	ammos: ComponentStore<AmmoComponent>,
	stage: Application['stage'],
	bounds: StageBounds,
	basePlatform: PlatformResource,
	spawnEnemy: SpawnEnemyFn
) {
	const tuning = world.getResource<Tuning>('tuning') ?? DEFAULT_TUNING;
	let nextY = world.getResource<number>('enemy-next-y') ?? basePlatform.y - tuning.enemyGapMin;
	const camera = world.getResource<CameraResource>('camera') ?? { y: 0, followThreshold: tuning.cameraFollowThreshold, peakY: 0 };
	while (nextY > camera.y - tuning.enemySpawnAhead) {
		const x = Math.random() < 0.5 ? tuning.enemyMarginX : bounds.width - tuning.enemyMarginX;
		spawnEnemy(world, enemies, transforms, graphics, ammoGraphics, ammos, stage, x, nextY);
		nextY -= randomInRange(tuning.enemyGapMin, tuning.enemyGapMax);
	}
	world.setResource<number>('enemy-next-y', nextY);
}


